import React from 'react';
import { Brain, Users, Zap, TrendingUp } from 'lucide-react';
import { FloatingAdinkra } from '../components/Decorators';
import { RevealOnScroll } from '../hooks/useScrollReveal';

const GROUPS = [
  {
    icon: Brain,
    title: 'Operations & Strategy',
    tagline: 'Designing the machine before building it',
    skills: [
      { name: 'Business Operations Architecture', level: 95, tag: 'Expert' },
      { name: 'Process Design & SOP Writing', level: 92, tag: 'Expert' },
      { name: 'Strategic Planning', level: 88, tag: 'Advanced' },
      { name: 'Regulatory & Compliance', level: 85, tag: 'Advanced' },
    ],
  },
  {
    icon: TrendingUp,
    title: 'Data & Analytics',
    tagline: 'Turning guesswork into decisions',
    skills: [
      { name: 'KPI Dashboards & Reporting', level: 94, tag: 'Expert' },
      { name: 'Data Modeling (Sheets / SQL)', level: 82, tag: 'Advanced' },
      { name: 'Revenue & Pricing Analysis', level: 88, tag: 'Advanced' },
      { name: 'Forecasting', level: 78, tag: 'Proficient' },
    ],
  },
  {
    icon: Zap,
    title: 'Technical',
    tagline: 'Enough code to ship',
    skills: [
      { name: 'JavaScript / HTML / CSS', level: 86, tag: 'Advanced' },
      { name: 'React & Modern Frontend', level: 78, tag: 'Proficient' },
      { name: 'Playwright Test Automation', level: 82, tag: 'Advanced' },
      { name: 'PWAs & Google Sheets APIs', level: 85, tag: 'Advanced' },
    ],
  },
  {
    icon: Users,
    title: 'Leadership & People',
    tagline: 'Rooted in Ghanaian values',
    skills: [
      { name: 'Team Building & Delegation', level: 92, tag: 'Expert' },
      { name: 'Stakeholder Management', level: 90, tag: 'Expert' },
      { name: 'Coaching & Training', level: 85, tag: 'Advanced' },
      { name: 'Conflict Resolution', level: 83, tag: 'Advanced' },
    ],
  },
];

const VALUES = [
  { title: 'Thinking', words: ['Analysing', 'Exploring', 'Innovative'] },
  { title: 'Connecting', words: ['Networking', 'Collaborating', 'Empathy'] },
  { title: 'Executing', words: ['Quality', 'Results Driven', 'Organized'] },
  { title: 'Progressing', words: ['Leadership', 'Resilience', 'Adaptability'] },
];

const SkillsPage = () => (
  <div className="kente-dark-bg" style={{ padding: '5rem 2rem', position: 'relative' }}>
    <FloatingAdinkra count={4} color="rgba(201, 168, 76, 0.04)" />
    <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
      <RevealOnScroll>
        <div className="section-header">
          <h2 className="section-title">Competencies & Values</h2>
          <p className="section-subtitle">Technical prowess blended with Ghanaian-rooted values</p>
        </div>
        <div className="kente-border"></div>
      </RevealOnScroll>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginTop: '2.5rem' }}>
        {GROUPS.map((group, idx) => {
          const Icon = group.icon;
          return (
            <RevealOnScroll key={group.title} delay={idx * 150} direction={idx % 2 === 0 ? 'left' : 'right'}>
              <div className="skill-card" style={{ background: 'rgba(26, 26, 26, 0.82)', borderColor: 'rgba(201,168,76,0.2)', padding: '1.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <Icon size={22} color="var(--gold)" />
                  <h3 style={{ fontSize: '1.15rem', color: 'var(--gold)', margin: 0, fontFamily: 'Cormorant Garamond, serif' }}>{group.title}</h3>
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--gold-satin)', opacity: 0.75, margin: '0 0 1.25rem', fontStyle: 'italic' }}>{group.tagline}</p>
                {group.skills.map((s) => (
                  <div key={s.name} className="skill-bar-wrap">
                    <div className="skill-bar-head">
                      <span>{s.name}</span>
                      <span className="level">{s.tag}</span>
                    </div>
                    <div className="skill-bar-track">
                      <div className="skill-bar-fill" style={{ width: `${s.level}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </RevealOnScroll>
          );
        })}
      </div>

      {/* Values */}
      <RevealOnScroll>
        <div className="section-header" style={{ marginTop: '4rem' }}>
          <h3 className="section-title" style={{ fontSize: '1.5rem' }}>Core Values</h3>
          <p className="section-subtitle">The principles I work from, not just the skills I work with</p>
        </div>
      </RevealOnScroll>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
        {VALUES.map((v, idx) => (
          <RevealOnScroll key={v.title} delay={idx * 120} direction="scale">
            <div className="skill-card" style={{ background: 'rgba(26, 26, 26, 0.6)', borderColor: 'rgba(201,168,76,0.15)', textAlign: 'center', padding: '1.5rem 1rem' }}>
              <h4 style={{ color: 'var(--gold)', margin: '0 0 0.85rem', fontSize: '0.95rem', letterSpacing: '0.08em' }}>{v.title}</h4>
              {v.words.map(w => (
                <div key={w} style={{ color: 'var(--gold-satin)', fontSize: '0.78rem', margin: '0.3rem 0' }}>{w}</div>
              ))}
            </div>
          </RevealOnScroll>
        ))}
      </div>
    </div>
  </div>
);

export default SkillsPage;
